import { Stack } from "@fluentui/react";
import { registerConfigurationAppListContainerStyles, registerConfigurationListContainerStyles, registerConfigurationListTotalContainerStyles, registerConfigurationListUserContainerStyles } from "../Styles/RegisterConfigurationStyles";
import { RegisterConfigurationAppTile } from "./RegisterConfigurationAppTile";
import { Row } from "../../../../Container/Grid/Row";
import { Col } from "../../../../Container/Grid/Col";
import { RegisterConfigurationUserInput } from "./RegisterConfigurationUserInput";
import { RegisterConfigurationTotal } from "./RegisterConfigurationTotal";
import { IRegisterContext, RegisterLoader, useRegisterContext } from "../../../RegisterContainer";
import { useCallback, useEffect, useRef, useState } from "react";
import { SystemDataLoadingStatus } from "../../../../../../Model/SystemModels";
import { SystemCore } from "../../../../../../Core/System/SystemCore";
import { ItemPrice, LeadQuoteDetails, PriceListType } from "../../../../../../Model/CommonModel";
import { RegisterConfigurationPriceList } from "./RegisterConfigurationPriceList";
import { RegisterService } from "../../../Service/RegisterService";

export const RegisterConfigurationAppList: React.FC = () => {
    const registerContext: IRegisterContext = useRegisterContext();
    const [dataStatus, setDataStatus] = useState<SystemDataLoadingStatus>(SystemDataLoadingStatus.ToLoad);
    const [fullUsersNo, setFullUsersNo] = useState<number>(registerContext.leadToLive.lead.full_Users_No === 0 ? 1 : registerContext.leadToLive.lead.full_Users_No);
    const fullUsersNoRef = useRef<number>(registerContext.leadToLive.lead.full_Users_No);
    const totalAmountRef = useRef<number>(0);
    const [totalAmount, setTotalAmount] = useState<number>(0);
    const [priceListType, setPriceListType] = useState<PriceListType>(registerContext.leadToLive.leadToLiveQualificationDetails.leadQuoteDetails[0].recurringInterval === "year" ? PriceListType.Yearly : PriceListType.Monthly);
    const [currency, setCurrency] = useState<string>("");
    const [apps, setApps] = useState<JSX.Element[]>([]);

    useEffect(() => {
        registerContext.setLeadToLive({
            ...registerContext.leadToLive,
            lead: {
                ...registerContext.leadToLive.lead,
                budget: totalAmountRef.current,
                full_Users_No: fullUsersNoRef.current
            }
        });        
    }, [totalAmount]);

    useEffect(() => {
        // Update the full users number in the lead
        fullUsersNoRef.current = fullUsersNo;
        
        // Calculate the total amount
        calculateTotalAmount();
    }, [fullUsersNo]);

    useEffect(() => {
        buildAppList(priceListType);
    }, [priceListType]);

    const updateQuoteStatus = useCallback((app: LeadQuoteDetails, index: number, array: LeadQuoteDetails[], isSelected: boolean, priceListType: PriceListType) => {
        let currentappList: LeadQuoteDetails[] = array;
        currentappList[index].selected = isSelected;
        currentappList[index].recurringInterval = priceListType === PriceListType.Yearly ? "year" : "month";
        currentappList[index].unitPrice = getPrice(app, priceListType);
        if(app.base)
            currentappList[index].quantity = fullUsersNoRef.current;
        else
            currentappList[index].quantity = 1;

        registerContext.setLeadToLive({
            ...registerContext.leadToLive,
            lead: {
                ...registerContext.leadToLive.lead,
                full_Users_No: fullUsersNoRef.current
            },
            leadToLiveQualificationDetails : {
                ...registerContext.leadToLive.leadToLiveQualificationDetails,
                leadQuoteDetails: currentappList,
            }
        });

    }, []);

    const buildAppList = (priceListType: PriceListType) => {
        setDataStatus(SystemDataLoadingStatus.Loading);
        let appList: JSX.Element[] = [];

        registerContext.leadToLive.leadToLiveQualificationDetails.leadQuoteDetails.forEach((app: LeadQuoteDetails, index: number, array: LeadQuoteDetails[]) => {
            appList.push(
                <Col>
                    <RegisterConfigurationAppTile
                        key={app.itemId}
                        title={app.description}
                        priceString={buildPriceString(app)}
                        informationText={app.extendedDescription}
                        selected={app.selected}
                        onChange={(isSelected: boolean) => {
                            if(app.mandatory && !isSelected)
                                return;

                            updateQuoteStatus(app, index, array, isSelected, priceListType);

                            buildAppList(priceListType);
                        }}
                        />
                </Col>
            );

            if (app.mandatory || app.selected)
                updateQuoteStatus(app, index, array, app.selected, priceListType);
        })
        
        setApps(appList);
        calculateTotalAmount();
        setDataStatus(SystemDataLoadingStatus.Loaded);
    }

    const getPrice = useCallback((currentApp: LeadQuoteDetails, priceListType: PriceListType) : number => {
        if(currentApp.prices.length > 0) {
            let price: ItemPrice | undefined = currentApp.prices.find((price: ItemPrice) => price.interval === priceListType);
            if(price) {
                return price.unitAmount;
            }
        }
        
        return 0;
    }, []);

    const buildPriceString = (currentApp: LeadQuoteDetails) : string => {
        let priceString: string = "";
        let currCurrency: string = "";

        if(currentApp.prices.length > 0) {
            let price: ItemPrice | undefined = currentApp.prices.find((price: ItemPrice) => price.interval === priceListType);
            if(price) {
                priceString = price.unitAmount.toString();
                currCurrency = price.currency;
            }
        }
        else
            priceString = currentApp.unitPrice.toString();

        switch(currCurrency) {
            case 'eur':
                priceString += "€";
                if(currency === "")
                    setCurrency("€");
                break;
            case 'usd':
                priceString += "$";
                if(currency === "")
                    setCurrency("$");
                break;
        }

        if(currentApp.base) {
            switch(priceListType) {
                case PriceListType.Yearly:
                    priceString += RegisterService.translate("configuration:userYear");
                    break;
                case PriceListType.Monthly:
                    priceString += RegisterService.translate("configuration:userMonth");
                    break;
            }
        }

        return priceString;
    }

    const calculateTotalAmount = () => {
        let selectedApps: LeadQuoteDetails[] = registerContext.leadToLive.leadToLiveQualificationDetails.leadQuoteDetails.filter((app: LeadQuoteDetails) => app.selected);
        let total: number = 0;
        
        selectedApps.forEach((app: LeadQuoteDetails) => {
            let price: ItemPrice | undefined = app.prices.find((price: ItemPrice) => price.interval === priceListType);
            if(price) {
                if(app.base)
                    total += price.unitAmount * fullUsersNoRef.current;
                else
                    total += price.unitAmount;
            }
            else {
                if(app.base)
                    total += app.unitPrice * fullUsersNoRef.current;
                else
                    total += app.unitPrice;
            }
        });

        totalAmountRef.current = total;
        setTotalAmount(total);
    }

    const validatePriceList = (priceListType: PriceListType) => {
        let selectedApps: LeadQuoteDetails[] = registerContext.leadToLive.leadToLiveQualificationDetails.leadQuoteDetails.filter((app: LeadQuoteDetails) => app.selected);

        let newSelectedApps: LeadQuoteDetails[] = selectedApps;
        selectedApps.forEach((app: LeadQuoteDetails, index: number, array: LeadQuoteDetails[]) => {
            newSelectedApps[index].recurringInterval = priceListType === PriceListType.Yearly ? "year" : "month";
        });

        registerContext.setLeadToLive({
            ...registerContext.leadToLive,
            leadToLiveQualificationDetails : {
                ...registerContext.leadToLive.leadToLiveQualificationDetails,
                leadQuoteDetails: newSelectedApps
            }
        });

        setPriceListType(priceListType);
    }

    return (
        <Stack
            verticalFill
            verticalAlign="space-between"
            className={registerConfigurationListContainerStyles}
        >
            <Stack
                horizontal
                horizontalAlign="space-between"
                verticalAlign="center"
                verticalFill
                className={registerConfigurationListUserContainerStyles}
            >
                <RegisterConfigurationPriceList
                    priceListType={priceListType}
                    onChange={(priceListType: PriceListType) => {validatePriceList(priceListType)}}
                />
                <RegisterConfigurationUserInput
                    userNo={fullUsersNo}
                    onChange={(userNo) => {userNo >= 0 && setFullUsersNo(userNo)}}
                />
            </Stack>
            {
                !SystemCore.isFinishedDataStatus(dataStatus) ?
                    <RegisterLoader 
                        label={RegisterService.translate("configuration:loader")}
                    />
                :
                    <Stack
        
                        className={registerConfigurationAppListContainerStyles}
                    >
                        <Row>
                            {apps}
                        </Row>
                    </Stack>
            }
            <Stack
                horizontal
                horizontalAlign="end"
                verticalAlign="center"
                verticalFill
                className={registerConfigurationListTotalContainerStyles}
            >
                <RegisterConfigurationTotal 
                    total={totalAmount}
                    currency={currency}
                />             
            </Stack>
        </Stack>
    );
};